import { Badge, Button, Card, CardHeader, ErrorState, LoadingRows, PageHeader } from '@shared/ui';
import { Link, useParams } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import { useClient } from '../hooks/use-clients';
import { describeTarget, targetParams } from '../schemas/target';

import type { ClientDetail, ClientPerson } from '../schemas/client';

/**
 * One client, in one environment.
 *
 * ## What it reports, and how confidently
 *
 * Whether anyone can sign in comes from identity-api's directory, not from
 * anything this app remembers. It briefly did remember — for want of that read
 * — and the gap showed here: a client with a working owner rendered as "not
 * onboarded through the Backoffice", beside an offer to add one.
 *
 * The read answers three ways, and the third is kept: people, nobody, or the
 * directory could not be asked. Only the second offers to add an owner.
 *
 * The effective-flags view the `OrgsPage` placeholder promises belongs here
 * too, and is not built: it needs a per-tenant flag read this screen does not
 * have yet.
 */
export function ClientDetailPage() {
    const { tenantId = '' } = useParams();
    const targetState = useClientTarget();
    const { target } = targetState;
    const client = useClient(target, tenantId);

    const search = new URLSearchParams(targetParams(target)).toString();

    if (client.isPending) return <LoadingRows label="Loading the client…" />;
    if (client.isError) {
        return <ErrorState error={client.error} onRetry={() => void client.refetch()} />;
    }
    if (!client.data) {
        // Not "no such client" — a client id is unique within ONE environment,
        // so the honest sentence names the one we looked in.
        return (
            <>
                <PageHeader eyebrow="Tenants" title="Not in this environment" />
                <Card>
                    <div className="p-5 text-[13.5px] text-gray-600">
                        <p>
                            No client with that id in <b>{describeTarget(target)}</b>. It may exist
                            in another environment — clients are not shared between them.
                        </p>
                        <div className="mt-4">
                            <Link to={{ pathname: '/clients', search }}>
                                <Button variant="ghost">Back to clients</Button>
                            </Link>
                        </div>
                    </div>
                </Card>
            </>
        );
    }

    const data = client.data;

    return (
        <>
            <PageHeader
                eyebrow="Tenants · Client"
                title={data.displayName}
                description={describeTarget(target)}
                actions={
                    <Link to={{ pathname: '/clients', search }}>
                        <Button variant="ghost">Back to clients</Button>
                    </Link>
                }
            />

            <div className="flex flex-col gap-5">
                <Card>
                    <CardHeader title="People" subtitle={peopleSubtitle(data)} />
                    <div className="p-5">
                        <PeopleSection client={data} search={search} />
                    </div>
                </Card>

                <Card>
                    <CardHeader title="Identity" />
                    <div className="p-5">
                        <dl className="grid grid-cols-[190px_1fr] text-[13.5px]">
                            <Row label="Tenant ID">
                                <span className="font-mono text-[12.5px]">{data.tenantId}</span>
                            </Row>
                            <Row label="Public address">
                                {/* The address a visitor uses — it is a URL
                                    segment in the membership and ticketing
                                    apps, which is why it is permanent. */}
                                {data.slug ? (
                                    <span className="font-mono text-[12.5px]">{data.slug}</span>
                                ) : (
                                    <span className="text-gray-400">None</span>
                                )}
                            </Row>
                            <Row label="Salesforce Org">
                                {data.orgId ? (
                                    <span className="font-mono text-[12.5px]">{data.orgId}</span>
                                ) : (
                                    <span className="text-gray-400">Not connected</span>
                                )}
                            </Row>
                            <Row label="SF subdomain">
                                {data.subdomain ?? <span className="text-gray-400">None</span>}
                            </Row>
                            <Row label="Type">
                                {data.isSandbox === true ? (
                                    <Badge tone="warning" dot>
                                        Sandbox
                                    </Badge>
                                ) : (
                                    <Badge tone="active" dot>
                                        {data.status}
                                    </Badge>
                                )}
                            </Row>
                            <Row label="Created">{formatDate(data.createdAt)}</Row>
                        </dl>
                    </div>
                </Card>
            </div>
        </>
    );
}

function peopleSubtitle(client: ClientDetail): string | undefined {
    if (!client.people) return undefined;
    return client.people.length === 1 ? '1 person' : `${String(client.people.length)} people`;
}

/**
 * Who can sign in, and how to reach them.
 *
 * Three states, and the third is not "probably fine": when the directory could
 * not be answered the honest report is that we do not know. The action stays
 * offered in that case — adding an owner to a client that already has one is
 * refused upstream, safely — but nothing is asserted either way.
 *
 * There is no login handle in this table, and that is the store's shape rather
 * than an omission: a federation handle lives in the access store as
 * `USERNAME#<handle>`, not on the directory row. Showing the email as if it
 * were what they type would be worse than not showing it.
 */
function PeopleSection({ client, search }: { client: ClientDetail; search: string }) {
    const addOwner = (
        <Link to={{ pathname: `/clients/${client.tenantId}/owner`, search }}>
            <Button>Add the owner</Button>
        </Link>
    );

    if (!client.people) {
        return (
            <div className="flex flex-col gap-4">
                <p className="text-[13px] text-gray-600">
                    The directory could not be reached, so who can sign into this client is unknown
                    — this is not a claim that nobody can.
                </p>
                <div>{addOwner}</div>
            </div>
        );
    }

    if (client.people.length === 0) {
        return (
            <div className="flex flex-col gap-4">
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                    <p className="text-[13px] font-semibold text-amber-900">
                        Nobody can sign into this client.
                    </p>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-amber-800">
                        The directory has no people for it. Do not create the client again — its
                        address is taken by this one.
                    </p>
                </div>
                <div>{addOwner}</div>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
                <thead>
                    <tr>
                        <PersonTh>Contact</PersonTh>
                        <PersonTh>Status</PersonTh>
                        <PersonTh>Since</PersonTh>
                    </tr>
                </thead>
                <tbody>
                    {client.people.map((person) => (
                        <PersonRow key={person.userId} person={person} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PersonTh({ children }: { children: React.ReactNode }) {
    return (
        <th className="whitespace-nowrap border-b border-gray-100 px-0 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {children}
        </th>
    );
}

function PersonRow({ person }: { person: ClientPerson }) {
    return (
        <tr>
            <td className="border-b border-gray-100 py-3 pr-4">
                {/* A mailto, because "contact info" that cannot be acted on is
                    just text an operator has to copy by hand. */}
                <a
                    href={`mailto:${person.email}`}
                    className="font-medium text-gray-900 hover:text-brand-700 hover:underline"
                >
                    {person.email}
                </a>
            </td>
            <td className="border-b border-gray-100 py-3 pr-4">
                {person.status === 'active' ? (
                    <Badge tone="success" dot>
                        {person.status}
                    </Badge>
                ) : (
                    // Anything that is not active is worth noticing: a disabled
                    // person still counts as "has people" and still cannot sign
                    // in, which is the one case a bare count would hide.
                    <Badge tone="warning" dot>
                        {person.status}
                    </Badge>
                )}
            </td>
            <td className="border-b border-gray-100 py-3 text-[12.5px] text-gray-500">
                {formatDate(person.createdAt)}
            </td>
        </tr>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <>
            <dt className="border-b border-gray-100 py-2.5 text-[12.5px] text-gray-500">{label}</dt>
            <dd className="m-0 break-words border-b border-gray-100 py-2.5">{children}</dd>
        </>
    );
}

function formatDate(iso: string): string {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime())
        ? '—'
        : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
